import { ParseIsoDate } from 'agu_app_shell';
import { ResearchGrantMatch } from '../../models/research.models';

/**
 * The two date helpers more than one module reads.
 *
 * They lived in `grant-finder/grant-card.component.ts` until review M-6: the
 * Saved board imported `DaysUntilClose` from a sibling module's CARD component,
 * and Grant Detail imported `FormatDayFirstDate` from the same file, which is
 * exactly what `modules/shared/` exists to prevent (`pinned-first.ts`,
 * `describe-error.ts`, `item-meta.ts`). Everything that is genuinely about ONE
 * grant card's copy — `FormatAwardLine`, `FormatClosingDate`,
 * `FormatClosingSuffix`, `FormatClosingLine` — stays with the card.
 */

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** The closing fields `DaysUntilClose` reads — a `Pick` so the Saved board can pass its own row's grant. */
export type GrantClosing = Pick<ResearchGrantMatch, 'closingDate' | 'closingIsRolling'>;

/**
 * Whole days from today to the closing date, or null when there is no dated
 * (or parseable) deadline. Both sides are CALENDAR dates, never instants (today
 * is the browser's LOCAL day, the closing date its UTC-midnight day):
 * comparing the deadline's midnight against a wall-clock `now` made a grant
 * closing today read `Closed` from 12:00 UTC onward (`Math.round` crossing
 * −0.5), while the server still counts it open — `closingDate < todayUtc` in
 * apps/MJAPI/src/resolvers/research/grantSearch.ts. The member-visible "today"
 * is the LOCAL calendar day: late evening US Eastern the UTC day is already
 * tomorrow, so a grant closing tomorrow read `today`.
 *
 * Shared because the Saved board needs the SAME number the card's own `Closes`
 * fact shows: it ran its own instant diff, so a grant closing today lost its
 * `Due … · 0d` chip from midday UTC while the card beside it still read
 * `today` (R-F20).
 */
export function DaysUntilClose(grant: GrantClosing, now: Date): number | null {
    if (grant.closingIsRolling) return null;
    const closing = ParseIsoDate(grant.closingDate);
    if (!closing) return null;
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((closing.getTime() - today) / MILLISECONDS_PER_DAY);
}

/**
 * "11 Sep 2026" — the portal's one date shape (`fmtDate`, grants.html), for a
 * bare `YYYY-MM-DD` day field, or `Not stated` when the value is missing or
 * unparseable.
 *
 * Day-first with a three-letter month and an UN-PADDED day, composed from
 * parts rather than `toLocaleDateString('en-GB')`: en-GB's own CLDR data
 * abbreviates September as "Sept", and the mockup prints "11 Sep 2026". en-US
 * supplies the three-letter month, the parts supply the mockup's day-first
 * order, and `day: 'numeric'` supplies its `(+d[2])` — every mockup date
 * helper drops the leading zero (`fmtDate`, grants.html:724; `fmt`,
 * detail.html:407; `fmt`, saved.html:370), so it reads "4 Sep 2026", never
 * "04 Sep 2026" (R-F18).
 *
 * Routed through `ParseIsoDate` rather than `new Date(`${isoDay}T00:00:00Z`)`
 * (review M-4): `formatToParts` on an Invalid Date raises
 * `RangeError: Invalid time value`, and every caller sits inside a template
 * expression, so one malformed `closingDate`/`postedDate` killed the whole
 * view. This is the same class of bug R-F20 routed `DaysUntilClose` through
 * `ParseIsoDate` for.
 *
 * Shared because every dated field in the portal reads this way — Grant
 * Detail's `Posted` fact used to print month-first `Aug 11, 2026` beside a
 * day-first `Closes` on the same tile grid (Task 8 live finding, R-F16).
 */
export function FormatDayFirstDate(isoDay: string | null | undefined): string {
    const day = ParseIsoDate(isoDay);
    if (!day) return 'Not stated';
    const parts = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).formatToParts(day);
    const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
    return `${part('day')} ${part('month')} ${part('year')}`;
}

/**
 * The browser's LOCAL calendar day ± `days` as `YYYY-MM-DD`. Never
 * `toISOString()`: that is the UTC day, which is already tomorrow late in the
 * evening in the Americas. The server's date-only parser takes the string as is.
 * Shared by the Grant and Job finders' relative-date filters.
 */
export function IsoDateOffsetDays(days: number, now: Date = new Date()): string {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    d.setDate(d.getDate() + days);
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
